import { convertFilterDefinitionForExtrasToFilterStoreValue } from 'libs/end-to-end/src/convert-filter-definition-for-extras-to-filter-store-value';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { determineSecondaryFilterDefinitionsForFeature } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getFilterDisplayName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { sortFilterValues } from 'libs/end-to-end/src/sorting';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { lookupCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';

export const resolveFocusAreaForCustomViz = (
  customVizRequest: CustomVizRequest,
): FocusArea | null => {
  return lookupCustomVizDefinition(
    customVizRequest.customVizId,
  ).resolveFocusArea(customVizRequest);
};

export const resolveAnalysisNameForCustomViz = (
  customVizRequest: CustomVizRequest,
): string | undefined => {
  return lookupCustomVizDefinition(
    customVizRequest.customVizId,
  ).resolveAnalysisName(customVizRequest);
};

const resolveFiltersForCustomViz = (
  customVizRequest: CustomVizRequest,
): FilterDefinitionForExtras[] | null => {
  return lookupCustomVizDefinition(customVizRequest.customVizId).resolveFilters(
    customVizRequest,
  );
};

export const constructBodyDisplayOfCustomViz = ({
  customVizRequest,
  myObservation,
  language,
  variant = 'longform',
}: {
  customVizRequest: CustomVizRequest;
  myObservation: MyObservation;
  language: Phrases;
  variant?: 'longform' | 'compressed';
}): {
  key: string;
  value: TranslatedContent;
}[] => {
  const { filterSetDefinitionsWithValues, focusAreas } = myObservation;

  const focusArea = resolveFocusAreaForCustomViz(customVizRequest);
  const filters = resolveFiltersForCustomViz(customVizRequest);
  const analysisName = resolveAnalysisNameForCustomViz(customVizRequest);

  const filterDefinitions = determineSecondaryFilterDefinitionsForFeature({
    feature: 'dataIntelligenceDeepDive',
    focusArea,
    filterSetDefinitionsWithValues,
    focusAreas,
    dimensionDefinitions: myObservation.dimensions,
    analysisName,
    pipelineAnalysisConfiguration: myObservation.pipelineAnalysisConfiguration,
  });

  if (!filters) {
    return [];
  }

  return filters.flatMap((filter) => {
    const asFilterStoreValue =
      convertFilterDefinitionForExtrasToFilterStoreValue({
        filterDefinitionForExtras: filter,
        filterSetDefinitions: filterDefinitions,
      });

    let value: TranslatedContent;

    switch (variant) {
      case 'compressed':
        value = convertFilterStoreValueToDisplayLongformTranslationCompressed({
          value: asFilterStoreValue,
          language,
        });
        break;
      case 'longform':
        value = convertFilterStoreValueToDisplayLongformTranslation({
          value: asFilterStoreValue,
          language,
        });
        break;
    }

    return {
      key: getFilterDisplayName(filter.columnHeaderValue, filterDefinitions),
      value,
    };
  });
};

export const convertFilterStoreValueToDisplayLongformTranslation = ({
  value,
  language,
}: {
  value: FilterStoreValue | null;
  language: Phrases;
}): TranslatedContent => {
  if (value === null) {
    return language.noSelection();
  }

  const type = inferTypeForFilterStore(value);

  if (type.type === 'string') {
    return language.dangerousUserInput({
      value: type.string,
    });
  }

  if (type.type === 'string[]') {
    return language.filterMultiSelectDisplayLongformPreview({
      items: sortFilterValues(type.stringArray),
    });
  }

  if (type.type === '[number, number]') {
    return language.numberRangeDisplay({
      start: type.numberRange[0],
      end: type.numberRange[1],
    });
  }

  if (type.type === 'date') {
    return language.dateDisplay({
      date: type.date,
    });
  }

  return language.noSelection();
};

export const convertFilterStoreValueToDisplayLongformTranslationCompressed = ({
  value,
  language,
}: {
  value: FilterStoreValue | null;
  language: Phrases;
}): TranslatedContent => {
  if (value === null) {
    return language.noSelection();
  }

  const type = inferTypeForFilterStore(value);

  if (type.type === 'string[]') {
    return language.filterMultiSelectDisplayLongformPreviewCompressed({
      items: sortFilterValues(type.stringArray),
    });
  }

  return convertFilterStoreValueToDisplayLongformTranslation({
    value,
    language,
  });
};
